import React from 'react';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { CardList } from '@/components/ui/CardList';

export function ProjectMaterialsList({ materials }: { materials: any[] }) {
  return (
    <div className="bg-white rounded-md border border-slate-100  overflow-hidden">
      <div className="p-4 border-b border-slate-50">
        <h3 className="text-sm font-normal text-slate-900 tracking-wider">Danh sách vật tư ({materials.length})</h3>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50 text-xs font-normal text-slate-400 border-b border-slate-100">
              <th className="py-3 px-4 w-10 font-normal">#</th>
              <th className="py-3 px-4 font-normal">Tên vật tư</th>
              <th className="py-3 px-4 font-normal">Hạng mục</th>
              <th className="py-3 px-4 text-center font-normal">ĐVT</th>
              <th className="py-3 px-4 text-right font-normal">Tồn kho</th>
              <th className="py-3 px-4 text-right font-normal">Đã sử dụng</th>
              <th className="py-3 px-4 text-right font-normal">Định mức</th>
              <th className="py-3 px-4 text-right font-normal">Giá trị tồn</th>
              <th className="py-3 px-4 text-center font-normal">Trạng thái</th>
              <th className="py-3 px-4 w-10 font-normal"></th>
            </tr>
          </thead>
          <tbody className="text-xs lg:text-sm divide-y divide-slate-50">
            {materials.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="py-4 px-4 text-slate-400 font-normal">{m.index}</td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-slate-50 p-1 flex items-center justify-center shrink-0 border border-slate-100 group-hover:border-emerald-200 transition-all">
                       <img src={m.image} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex flex-col min-w-0">
                       <span className="font-normal text-slate-700 truncate group-hover:text-emerald-600 transition-colors cursor-pointer">{m.name}</span>
                       <span className="text-xs text-slate-400 font-normal">{m.subName}</span>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                   <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal", m.categoryColor)}>{m.category}</span>
                </td>
                <td className="py-4 px-4 text-center font-normal text-slate-500">{m.unit}</td>
                <td className="py-4 px-4 text-right text-slate-900 font-normal">{m.stock}</td>
                <td className="py-4 px-4 text-right text-slate-500 font-normal">{m.used}</td>
                <td className="py-4 px-4 text-right text-slate-400 font-normal">{m.minStock}</td>
                <td className="py-4 px-4 text-right text-slate-900 font-normal">{formatCurrency(m.value, '')}</td>
                <td className="py-4 px-4 text-center">
                   <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal whitespace-nowrap", m.statusColor)}>{m.status}</span>
                </td>
                <td className="py-4 px-4 text-center">
                   <ActionMenu items={[{ label: 'Chi tiết vật tư' }, { label: 'Nhập kho' }, { label: 'Xuất kho' }, { label: 'Xóa', destructive: true }]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List */}
      <div className="lg:hidden p-4">
        <CardList 
          items={materials}
          renderCard={(m) => (
            <div key={m.id} className="bg-white p-3 rounded-md border border-slate-100 space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-slate-50 p-1 border border-slate-100">
                    <img src={m.image} alt="" className="w-full h-full object-contain" />
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">{m.type}</span>
                    <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal", m.categoryColor)}>{m.category}</span>
                  </div>
                </div>
                <ActionMenu items={[{ label: 'Chi tiết' }, { label: 'Nhập/Xuất' }]} />
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-slate-50 pt-3 text-xs">
                 <div className="space-y-1">
                    <p className="text-slate-400 font-normal">Tồn kho / Định mức</p>
                    <p className="text-xs font-normal text-slate-400">{m.type}</p>
                    <p className="font-normal text-slate-900">{m.stock} / {m.minStock} {m.unit}</p>
                 </div>
                 <div className="space-y-1 text-right">
                    <p className="text-slate-400 font-normal">Giá trị tồn</p>
                    <p className="font-normal text-slate-900">{formatCurrency(m.value, '')}</p>
                 </div>
              </div>
              <div className="flex justify-between items-center bg-slate-50/50 p-2 rounded-md">
                 <span className="text-xs font-normal text-slate-500">{m.unit}</span>
                 <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal", m.statusColor)}>{m.status}</span>
              </div>
            </div>
          )}
        />
      </div>

      <div className="px-4 border-t border-slate-50 bg-slate-50/30">
        <TablePagination currentPage={1} totalItems={156} pageSize={10} onPageChange={() => {}} itemLabel="vật tư" />
      </div>
    </div>
  );
}
